import { createApi } from "@reduxjs/toolkit/query/react";
import { components } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

// Define the types from the API schema
type MarginPeriod = components["schemas"]["MarginPeriod"];
type ApiResponseMarginPeriodArray =
  components["schemas"]["ApiResponse_MarginPeriod-Array_"];
type ApiResponseMarginPeriod =
  components["schemas"]["ApiResponse_MarginPeriod_"];
type CreateMarginPeriodInput = components["schemas"]["CreateMarginPeriodInput"];
type UpdateMarginPeriodInput = components["schemas"]["UpdateMarginPeriodInput"];
type MarginsCampaign = components["schemas"]["MarginsCampaign"];
type ApiResponseMarginsCampaign =
  components["schemas"]["ApiResponse_MarginsCampaign_"];
type ApiResponseBoolean = components["schemas"]["ApiResponse_boolean_"];
type CampaignMarginPeriod = components["schemas"]["CampaignMarginPeriod"];
type ApiResponseCampaignMarginPeriodArray =
  components["schemas"]["ApiResponse_CampaignMarginPeriod-Array_"];

export const metricsApi = createApi({
  reducerPath: "metricsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Metrics"],
  endpoints: (builder) => ({
    // Get all margin periods for an internal campaign
    getMarginsByInternalCampaignId: builder.query<
      MarginPeriod[],
      { id: string }
    >({
      query: (params) => {
        return `/margins/${params.id}/margins`;
      },
      transformResponse: (response: ApiResponseMarginPeriodArray) =>
        response.data as MarginPeriod[],
      providesTags: ["Metrics"],
    }),

    // Add a new margin period for an internal campaign
    addMarginPeriod: builder.mutation<
      MarginPeriod,
      { id: string; marginPeriod: CreateMarginPeriodInput }
    >({
      query: (params) => ({
        url: `/margins/${params.id}/margins`,
        method: "POST",
        body: params.marginPeriod,
      }),
      transformResponse: (response: ApiResponseMarginPeriod) =>
        response.data as MarginPeriod,
      invalidatesTags: ["Metrics"],
    }),

    // Get all margin periods for all internal campaigns associated with a campaign
    getMarginsByCampaignId: builder.query<MarginsCampaign, { id: string }>({
      query: (params) => {
        return `/margins/campaign/${params.id}/margins`;
      },
      transformResponse: (response: ApiResponseMarginsCampaign) =>
        response.data as MarginsCampaign,
      providesTags: ["Metrics"],
    }),

    // Delete a margin period by ID
    deleteMarginPeriod: builder.mutation<boolean, { id: string }>({
      query: (params) => ({
        url: `/margins/margin-period/${params.id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponseBoolean) =>
        response.data as boolean,
      invalidatesTags: ["Metrics"],
    }),

    // Update a margin period by ID
    updateMarginPeriod: builder.mutation<
      MarginPeriod,
      { id: string; marginPeriod: UpdateMarginPeriodInput }
    >({
      query: (params) => ({
        url: `/margins/margin-period/${params.id}`,
        method: "PUT",
        body: params.marginPeriod,
      }),
      transformResponse: (response: ApiResponseMarginPeriod) =>
        response.data as MarginPeriod,
      invalidatesTags: ["Metrics"],
    }),

    // Get all raw margin period entries from the database for an internal campaign
    getRawMarginsByInternalCampaignId: builder.query<
      CampaignMarginPeriod[],
      { id: string }
    >({
      query: (params) => `/margins/${params.id}/raw-margins`,
      transformResponse: (response: ApiResponseCampaignMarginPeriodArray) =>
        response.data as CampaignMarginPeriod[],
      providesTags: ["Metrics"],
    }),
  }),
});

export const {
  useGetMarginsByInternalCampaignIdQuery,
  useAddMarginPeriodMutation,
  useGetMarginsByCampaignIdQuery,
  useDeleteMarginPeriodMutation,
  useUpdateMarginPeriodMutation,
  useGetRawMarginsByInternalCampaignIdQuery,
} = metricsApi;
